import { useCallback, useState } from "react";

import type { AsyncListStatus } from "./use-async-list";

export interface UseLastKnownTotalResult {
  /** Último total de uma resposta com sucesso; `undefined` até a primeira. */
  lastTotal: number | undefined;
  /** Registra o resultado atual da lista; chame no render, depois do `useAsyncList`. */
  remember: (status: AsyncListStatus, total: number) => void;
}

/**
 * Guarda o último total conhecido de uma lista assíncrona, para passar a `useTableState({ total })`:
 * durante o `loading` e em erro o `total` da lista é 0 e zeraria a página no meio do clique.
 * É um par (`lastTotal`, `remember`) porque o `useTableState` precisa do total antes de a lista existir
 * (os `params` da lista saem dele): declare o hook primeiro e chame `remember` depois do `useAsyncList`.
 */
export function useLastKnownTotal(): UseLastKnownTotalResult {
  const [lastTotal, setLastTotal] = useState<number | undefined>(undefined);

  // Só grava quando o valor muda: `setState` no render sempre agenda outra passada, e sem a guarda entraria em loop.
  const remember = useCallback(
    (status: AsyncListStatus, total: number) => {
      if (status === "success" && total !== lastTotal) setLastTotal(total);
    },
    [lastTotal],
  );

  return { lastTotal, remember };
}
